import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { ThunkDispatch } from 'redux-thunk';
import { PricingApi } from '../../../../api/api.apiclient';
import { DATE_FORMAT, GatewayResponse, MONTH_YEAR_FORMAT } from '../../../../api/api.types';
import {
  BrokerRateModel,
  CarModelModel,
  DeskGroupModel,
  DeskModel,
  ItemModel,
  ProductModel
} from '../../../../api/core/core.types';
import {
  CoreCacheStatus,
  DatedRatePeriod,
  DatedRatePeriodMandatoryItemModel,
  GroupBy,
  ListAllRatesParameters,
  PriceCalculatorCacheStatus,
  RatePeriod,
  RatePlanListAllModel,
  RatesGroupedByRentalDays,
  RatesOverviewElementModel,
  RatesSelectedItem,
  RateStopSellParameters,
  StopSellPeriod,
  StopSellRuleType,
  StopSellsOverviewElementModel
} from '../../../../api/pricing/pricing.types';
import CacheStatusLabel from '../../../../components/forms/CacheStatusLabel';
import DropDownWithLabel from '../../../../components/forms/DropDownWithLabel';
import { RatesOverviewTable } from '../../../../components/ratecomponents/RatesOverviewTable';
import { sortRatePlansByLocationGroup } from '../../../../helpers/rateplan.helper';
import { AppState } from '../../../../store/store.types';
import { STORED_DESK_GROUP_ID, STORED_PRODUCT_ID } from '../../../../types/app.index';
import { getAllBrokerRatesList } from '../../../setup/actions/brokerrate.actions';
import { getCarModelsList } from '../../../setup/actions/carmodel.actions';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { getDeskGroupList } from '../../../setup/actions/deskgroup.actions';
import { getProductList } from '../../../setup/actions/product.actions';
import { getItemPriceList } from '../../actions/itemprice.actions';
import { getCoreCacheStatus, getRateCacheStatus } from '../../actions/ratecachestatus.actions';
import {
  getRatePlanList,
  saveDeskGroupId,
  saveProductId,
  storeBrokerRateCode,
  storeDeskCode,
  storeStartingDate
} from '../../actions/rateplan.actions';
import { RatePlanActionTypes } from '../../types/rateplan.types';
import dayjs, { Dayjs } from 'dayjs';
import RatesTimeline from '../../../../components/ratecomponents/RatesTimeline';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getItemList } from '../../../setup/actions/item.actions';

interface RatesOverviewProps extends RouteComponentProps, WithTranslation {
  getRatePlanList: (productId?: number) => void;
  getProductList: () => void;
  getDeskGroupList: () => void;
  getCarModelList: () => void;
  getAllBrokerRates: () => void;
  getAllDesks: () => void;
  getItemPrices: () => void;
  getItemList: () => void;
  saveProductId: (id: number) => void;
  getRateCacheStatus: () => void;
  storeDeskCode: (code: string) => void;
  storeBrokerRateCode: (code: string) => void;
  storeStartingDate: (date: string) => void;
  saveDeskGroupId: (id: number) => void;
  getCoreCacheStatus: () => void;
  brokerRates: BrokerRateModel[];
  desks: DeskModel[];
  items: ItemModel[];
  ratePlans: RatePlanListAllModel[];
  carModels: CarModelModel[];
  products: ProductModel[];
  deskGroups: DeskGroupModel[];
  selectedProductId: number | null;
  rateCacheStatus: PriceCalculatorCacheStatus | null;
  selectedDeskGroupId: number | null;
  selectedBrokerRateCode: string | null;
  selectedDeskCode: string | null;
  selectedStartingDate: string | null;
  coreCacheStatus: CoreCacheStatus | null;
}

interface RatesOverviewState {
  rates: RatesOverviewElementModel[];
  startDate: { value: string; label: string } | undefined;
  endDate: Dayjs;
  brokerRateCode: string | undefined;
  deskCode: string | undefined;
  groupBy: GroupBy;
  largestPerDay: number;
  smallestPerDay: number;
  ratePlansToBeRendered: any | undefined;
  isLoading: boolean;
  initialLoadFinished: boolean;
  stopSells: StopSellsOverviewElementModel[];
  selectedProduct: { value: string; label: string } | undefined;
  selectedDesk: { value: string; label: string } | undefined;
  selectedBrokerRate: { value: string; label: string } | undefined;
  selectedDeskGroup: { value: string; label: string } | undefined;
  filtersUpdated: boolean;
}

const DURATION_OF_OVERVIEW = 3;

class RatesOverview extends React.PureComponent<RatesOverviewProps, RatesOverviewState> {

  readonly state = {
    rates: [],
    startDate: { value: dayjs().format(DATE_FORMAT), label: dayjs().format(MONTH_YEAR_FORMAT) },
    endDate: dayjs().startOf('day').add(DURATION_OF_OVERVIEW, 'months'),
    brokerRateCode: undefined,
    deskCode: undefined,
    groupBy: GroupBy.CAR_MODEL,
    largestPerDay: 0,
    smallestPerDay: 0,
    ratePlansToBeRendered: undefined,
    isLoading: false,
    initialLoadFinished: false,
    stopSells: [] as StopSellsOverviewElementModel[],
    selectedProduct: undefined,
    selectedDesk: undefined,
    selectedBrokerRate: undefined,
    selectedDeskGroup: undefined,
    filtersUpdated: false
  };

  async componentDidMount() {
    this.props.getProductList();
    this.props.getCarModelList();
    this.props.getDeskGroupList();
    this.props.getAllBrokerRates();
    this.props.getAllDesks();
    this.props.getItemPrices();
    this.props.getRateCacheStatus();
    this.props.getCoreCacheStatus();
    this.props.getItemList();
    await this.loadDeskGroups();
    await this.loadProducts();
    this.setState({ initialLoadFinished: true });
  }

  componentDidUpdate(prevProps: RatesOverviewProps) {
    if (!this.props.rateCacheStatus) this.props.getRateCacheStatus();

    if (this.props.products !== prevProps.products && this.state.initialLoadFinished) {
      this.loadProducts();
    }


    if (this.props.deskGroups !== prevProps.deskGroups && this.state.initialLoadFinished) this.loadDeskGroups();
  }

  loadProducts = async () => {
    if (!this.props.products.length) await this.props.getProductList();

    if (this.props.products.length) {

      if (this.props.selectedProductId) {
        const product = this.props.products.find((p) => p.id === this.props.selectedProductId);

        if (product) {
          await this.selectProduct(product);
          this.loadBrokerRates();
          return;
        }

      } else {
        const storedProductId = localStorage.getItem(STORED_PRODUCT_ID);
        if (storedProductId) {
          const productId = JSON.parse(storedProductId) as number;

          if (productId) {
            const product = this.props.products.find((p) => p.id === productId);

            if (product) {
              await this.selectProduct(product);
              this.loadBrokerRates();
              return;
            }
          }
        }
      }

      const product = this.props.products[ 0 ];
      if (product) {
        await this.selectProduct(product);
        this.loadBrokerRates();
      }
    }
  };

  loadBrokerRates = async () => {
    if (!this.props.brokerRates.length) await this.props.getAllBrokerRates();

    if (this.props.brokerRates.length) {

      if (this.props.selectedBrokerRateCode) {
        const brokerRate = this.props.brokerRates.find((br) => br.code === this.props.selectedBrokerRateCode);

        if (brokerRate && this.props.selectedProductId && brokerRate.productId === this.props.selectedProductId) {
          this.selectBrokerRate(brokerRate);
          return;
        }
      }

      if (this.props.selectedProductId) {
        const filteredBrokerRates = this.props.brokerRates.filter((br) => br.productId === this.props.selectedProductId);

        if (filteredBrokerRates.length) {
          this.selectBrokerRate(filteredBrokerRates[ 0 ]);
        }
      }
    }
  };

  loadDeskGroups = async () => {
    if (!this.props.deskGroups.length) await this.props.getDeskGroupList();

    if (this.props.deskGroups.length) {

      if (this.props.selectedDeskGroupId) {
        const deskGroup = this.props.deskGroups.find((dg) => dg.id === this.props.selectedDeskGroupId);

        if (deskGroup) {
          await this.selectDeskGroup(deskGroup);
          this.loadDesks();
          return;
        }

      } else {
        const storedDeskGroupId = localStorage.getItem(STORED_DESK_GROUP_ID);
        if (storedDeskGroupId) {
          const deskGroupId = JSON.parse(storedDeskGroupId) as number;

          if (deskGroupId) {
            const deskGroup = this.props.deskGroups.find((dg) => dg.id === deskGroupId);

            if (deskGroup) {
              await this.selectDeskGroup(deskGroup);
              this.loadDesks();
              return;
            }
          }
        }
      }

      const deskGroup = this.props.deskGroups[ 0 ];
      if (deskGroup) {
        await this.selectDeskGroup(deskGroup);
        this.loadDesks();
      }
    }
  };

  loadDesks = async () => {
    if (!this.props.desks.length) await this.props.getAllDesks();

    if (this.props.desks.length) {

      if (this.props.selectedDeskCode) {
        const desk = this.props.desks.find((d) => d.code === this.props.selectedDeskCode);

        if (desk && this.props.selectedDeskGroupId && desk.group.id === this.props.selectedDeskGroupId) {
          this.selectDesk(desk);
          return;
        }
      }

      if (this.props.selectedDeskGroupId) {
        const filteredDesks = this.props.desks.filter((d) => d.group.id === this.props.selectedDeskGroupId);

        if (filteredDesks.length) {
          this.selectDesk(filteredDesks[ 0 ]);
        }
      }
    }
  };

  getAllStopSells = async (brokerRateCode: string, deskCode: string) => {
    const parameters: RateStopSellParameters = {
      brokerRateCode,
      deskCode,
      startDate: this.state.startDate.value,
      endDate: this.state.endDate.format(DATE_FORMAT)
    };

    PricingApi.RatesOverview.listStopSellsForRates(parameters).then((response) => {
      this.setState({ stopSells: response.data });
    });
  };

  getAllRates = async (brokerRateCode: string, deskCode: string) => {
    this.setState({ isLoading: true, rates: [], stopSells: [] });

    const parameters: ListAllRatesParameters = {
      startDate: this.state.startDate.value,
      endDate: this.state.endDate.format(DATE_FORMAT),
      brokerRateCode,
      deskCode,
      groupBy: this.state.groupBy
    };

    PricingApi.RatesOverview.listAllRates(parameters).then((response: GatewayResponse<RatesOverviewElementModel[]>) => {
      this.setState({ rates: response.data, isLoading: false });
    });
  };

  getBrokerRateOptions = () => {
    const brokerRateOptions: { value: string; label: string, code: string }[] = [];

    this.props.brokerRates.forEach((br) => {
      brokerRateOptions.push({ value: br.code, label: br.name, code: br.code });
    });

    // eslint-disable-next-line no-nested-ternary
    return brokerRateOptions.sort((a, b) => ((a.label < b.label) ? -1 : (a.label > b.label) ? 1 : 0));
  };

  groupRatesByCarModel = () => {
    const rates = this.state.rates;
    const ratesByCarModelMap = new Map();
    let maxMin = { largestAmount: 0, smallestAmount: 0 };

    rates.forEach((overViewElement: RatesOverviewElementModel) => {
      const startDate = overViewElement.startDate;
      const endDate = overViewElement.endDate;

      overViewElement.ratePeriods.forEach((period) => {
        const datedRatePeriod: DatedRatePeriod = {
          baseRateAdjustmentAmount: period.rate.baseRateAdjustmentAmount,
          baseRateAdjustmentPercentage: period.rate.baseRateAdjustmentPercentage,
          discountAmount: period.rate.discountAmount,
          discountPercentage: period.rate.discountPercentage,
          safAmount: period.rate.safAmount,
          safPercentage: period.rate.safPercentage,
          vatAmount: period.rate.vatAmount,
          vatPercentage: period.rate.vatPercentage,
          adjustedVehicleBaseRateAmount: period.rate.adjustedVehicleBaseRateAmount,
          mandatoryItems: this.getMandatoryItemList(period),
          startDate,
          endDate,
          mandatoryItemPriceTotal: period.rate.mandatoryItemPriceTotal,
          totalPerDay: period.rate.totalPerDay,
          isAdjustment: period.rate.isAdjustment,
          baseAmount: period.rate.baseRateAmount

        };

        const stopSells: StopSellPeriod[] = [];
        if (this.state.stopSells.length) {
          const carModel = this.props.carModels.find((cm) => cm.id === period.carModelId);
          if (carModel) {
            this.state.stopSells.forEach((ss: StopSellsOverviewElementModel) => {
              if (ss.acrissCode === carModel.acrissCode || ss.acrissCode === 'ALL') {
                stopSells.push(...ss.stopSells);
              }
            });
            maxMin = this.getBlockShortRentalsStopSellsMaxMin(startDate, stopSells, maxMin, period);
          }
        } else {
          maxMin = this.getMaxMin(maxMin.largestAmount, maxMin.smallestAmount, period.rate.totalPerDay);
        }


        if (!(ratesByCarModelMap.has(period.carModelId))) {

          const ratesByRentalDays: RatesGroupedByRentalDays = {
            rentalDays: period.rentalDays,
            datedRatePeriods: [ datedRatePeriod ],
            stopSells
          };

          ratesByCarModelMap.set(period.carModelId, [ ratesByRentalDays ]);

        } else {
          const ratesByRentalDaysList = ratesByCarModelMap.get(period.carModelId);
          let ratesByRentalDays = ratesByRentalDaysList.find((r: RatesGroupedByRentalDays) => period.rentalDays === r.rentalDays);

          if (ratesByRentalDays) {
            ratesByRentalDays.datedRatePeriods.push(datedRatePeriod);
          } else {
            ratesByRentalDays = {
              rentalDays: period.rentalDays,
              datedRatePeriods: [ datedRatePeriod ],
              stopSells
            };

            ratesByRentalDaysList.push(ratesByRentalDays);
          }
        }
      });
    });

    this.setState({ largestPerDay: maxMin.largestAmount, smallestPerDay: maxMin.smallestAmount });

    const keys: string[] = Array.from(ratesByCarModelMap.keys());

    keys.forEach((k) => {
      let ratesForCarClass = ratesByCarModelMap.get(k);
      ratesForCarClass = ratesForCarClass.sort((a: RatesGroupedByRentalDays, b: RatesGroupedByRentalDays) => {
        if (a.rentalDays && b.rentalDays) return a.rentalDays - b.rentalDays;

        return 0;
      });
    });

    return ratesByCarModelMap;
  };

  getBlockShortRentalsStopSellsMaxMin = (startDate: string, stopSells: StopSellPeriod[], maxMin: any, period: RatePeriod) => {
    const rateStartDate = dayjs(startDate);
    const pricedStopSells = stopSells.filter((ssp) => {
      const stopSellStartDate = dayjs(ssp.startDateTime).toDate();
      const stopSellEndDate = dayjs(ssp.endDateTime).toDate();
      return rateStartDate.toDate() >= stopSellStartDate &&
        rateStartDate.toDate() <= stopSellEndDate;
    });

    if (!pricedStopSells.length) {
      maxMin = this.getMaxMin(maxMin.largestAmount, maxMin.smallestAmount, period.rate.totalPerDay);
    } else {
      pricedStopSells.forEach((pss) => {
          if (pss.ruleType === StopSellRuleType.BLOCK_SHORT_RENTALS && pss.rentalDays < period.rentalDays) {
            maxMin = this.getMaxMin(maxMin.largestAmount, maxMin.smallestAmount, period.rate.totalPerDay);
          }
        }
      );
    }

    return maxMin;
  };

  getMaxMin = (largestAmount: number, smallestAmount: number, totalPerDay: number) => {
    if (!largestAmount && !smallestAmount) {
      largestAmount = totalPerDay;
      smallestAmount = totalPerDay;
    } else if (totalPerDay > largestAmount) {
      largestAmount = totalPerDay;
    } else if (totalPerDay < smallestAmount) {
      smallestAmount = totalPerDay;
    }

    return { largestAmount: largestAmount, smallestAmount: smallestAmount };
  };

  getMandatoryItemList = (period: RatePeriod) => {
    const mandatoryItems: DatedRatePeriodMandatoryItemModel[] = [];
    period.rate.mandatoryItems.map((mandatoryItem) =>
      mandatoryItems.push(this.getItem(mandatoryItem.id, mandatoryItem.price) as DatedRatePeriodMandatoryItemModel)
    );
    return mandatoryItems;
  };

  getItem = (itemId: number, price: number) => {
    if (this.props.items) {
      const itemModel = this.props.items.find((item: ItemModel) => item.id === itemId);
      if (itemModel) {
        return {
          name: itemModel.name,
          price: price
        };
      }
    }
  };

  onDateChange = async (value: any) => {
    if (!dayjs(value).isSame(dayjs(this.state.startDate.value))) {
      const valueCopy = value;
      const endDate = dayjs(valueCopy).add(DURATION_OF_OVERVIEW, 'months').startOf('day');
      this.setState({ startDate: { value, label: dayjs(value).format(MONTH_YEAR_FORMAT) }, endDate },
        () => {
          const brokerRateCode = this.state.brokerRateCode;
          const deskCode = this.state.deskCode;

          if (brokerRateCode && deskCode) {
            this.getAllRates(brokerRateCode, deskCode);
            this.getAllStopSells(brokerRateCode, deskCode);
          }
        });

      this.props.storeStartingDate(value);
      this.onGetRatesClick();
    }
  };

  selectProduct = (product: ProductModel) => {
    this.setState({ selectedProduct: { value: String(product.id), label: product.name } });
    this.props.saveProductId(product.id);
  };

  onBrokerRateSelect = (value: string) => {
    const brokerRate = this.props.brokerRates.find((br) => br.code === value);

    if (brokerRate) {
      let product!: ProductModel;
      this.props.products.map((pr) => {
        if (pr.code === brokerRate.productCode) {
          product = pr;
        }
      });
      this.selectProduct(product);
      this.props.storeBrokerRateCode(brokerRate.code);
      this.setState({
          brokerRateCode: brokerRate.code, selectedBrokerRate: { value: String(brokerRate.code), label: brokerRate.name },
          filtersUpdated: true
        },
        () => this.onGetRatesClick());
    }
  };

  selectBrokerRate = async (brokerRate: BrokerRateModel) => {
    this.setState({ selectedBrokerRate: { value: String(brokerRate.code), label: brokerRate.name } });
    this.props.getRatePlanList(brokerRate.productId);

    const deskCode = this.state.deskCode;
    if (deskCode) {
      await this.getAllRates(brokerRate.code, deskCode);
      await this.getAllStopSells(brokerRate.code, deskCode);

      const desk = this.props.desks.find((d) => d.code === deskCode);
      if (desk && desk.group && desk.group.id) {
        this.sortRatePlans(desk.group.id);
      }

      this.props.storeBrokerRateCode(brokerRate.code);
      this.setState({ brokerRateCode: brokerRate.code });

    } else {
      this.props.storeBrokerRateCode(brokerRate.code);
      this.setState({ brokerRateCode: brokerRate.code });
    }
  };

  selectDeskGroup = (deskGroup: DeskGroupModel) => {
    this.setState({
      selectedDeskGroup: { value: String(deskGroup.id), label: deskGroup.name }
    });
    if (deskGroup.id) this.props.saveDeskGroupId(deskGroup.id);
  };

  onDeskSelect = (value: string) => {
    const desk = this.props.desks.find((d) => d.code === value);

    if (desk) {
      this.selectDeskGroup(desk.group);

      this.props.storeDeskCode(desk.code);
      this.setState({ deskCode: desk.code, selectedDesk: { value: desk.code, label: desk.name }, filtersUpdated: true },
        () => this.onGetRatesClick());
    }
  };

  selectDesk = async (desk: DeskModel) => {
    const brokerRateCode = this.state.brokerRateCode;

    if (brokerRateCode) {
      await this.getAllRates(brokerRateCode, desk.code);
      await this.getAllStopSells(brokerRateCode, desk.code);

      if (desk && desk.group && desk.group.id) {
        this.sortRatePlans(desk.group.id);
        this.setState({ selectedDesk: { value: desk.code, label: desk.name } });
      }

      this.props.storeDeskCode(desk.code);
      this.setState({ deskCode: desk.code });

    } else {
      this.props.storeDeskCode(desk.code);
      this.setState({ deskCode: desk.code, selectedDesk: { value: desk.code, label: desk.name } });
    }
  };

  onGetRatesClick = async () => {
    const brokerRate = this.props.brokerRates.find((br) => br.code === this.state.brokerRateCode);
    const desk = this.props.desks.find((d) => d.code === this.state.deskCode);

    if (brokerRate && desk) {
      this.props.getRatePlanList(brokerRate.productId);
      await this.getAllRates(brokerRate.code, desk.code);
      await this.getAllStopSells(brokerRate.code, desk.code);
      await this.props.getRateCacheStatus();

      if (desk && desk.group && desk.group.id) {
        this.sortRatePlans(desk.group.id);
      }

      this.setState({ filtersUpdated: false });
    }
  };

  generatePeriodStartOptions = () => {
    const options = [ { value: dayjs().format(DATE_FORMAT), label: dayjs().format(MONTH_YEAR_FORMAT) } ];
    for (let i = 1; i <= 15; i++) {
      const date = dayjs().add(i, 'month');
      options.push({ value: date.format(DATE_FORMAT), label: date.format(MONTH_YEAR_FORMAT) });
    }

    return options;
  };

  sortRatePlans = async (deskGroupId: number) => {
    if (!this.props.ratePlans.length && this.props.selectedProductId) await this.props.getRatePlanList(this.props.selectedProductId);
    const sortedRatePlans = sortRatePlansByLocationGroup(this.props.ratePlans, this.props.deskGroups);
    const deskGroup = this.props.deskGroups.find((dg) => dg.id === deskGroupId);
    if (deskGroup) {
      const ratePlansByDeskGroup = new Map([
        [ deskGroup.name, sortedRatePlans.get(deskGroup.name) ] ]);
      this.setState({ ratePlansToBeRendered: ratePlansByDeskGroup });
    }
  };

  onPublish = async () => {
    await PricingApi.RatePlan.publishRatePlans();
    this.props.getRateCacheStatus();
    this.props.getCoreCacheStatus();
  };

  render() {
    const { t } = this.props;

    return (
        <main>
          <div className="page-content">
            <div className="mt-2 mb-2">
              <h1 className="rates-h1">{t('page_title.rates_overview')}</h1>
            </div>
            <hr className="mt-0 mb-0"/>
            <div className="d-flex justify-content-between mb-2 mt-2">
              <div className="d-flex justify-content-start align-items-center">
                <div className="row d-flex align-items-center ml-2 rates-dropdown-element-container">
                  <DropDownWithLabel
                    label={t('l.rates_overview.broker_rate')}
                    content={this.getBrokerRateOptions()}
                    filterFields={[ 'label', 'code' ]}
                    displayAttributes={{
                      primary: 'code',
                      secondary: 'label'
                    }}
                    selected={this.state.selectedBrokerRate}
                    handleChange={this.onBrokerRateSelect}
                  />
                </div>
                <div className="row align-items-center ml-2 rates-dropdown-element-container">
                  <DropDownWithLabel
                    label={t('l.rates_overview.desk')}
                    filterFields={[ 'label', 'value' ]}
                    displayAttributes={{
                      primary: 'label',
                      secondary: 'value'
                    }}
                    content={this.props.desks.sort((a, b) => ((a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0)).map((sd) => ({
                      value: sd.code,
                      label: sd.name
                    }))}
                    selected={this.state.selectedDesk}
                    handleChange={this.onDeskSelect}
                  />
                </div>
                <div className="row align-items-center ml-2 rates-dropdown-element-container">
                  <DropDownWithLabel
                    label={t('l.rates_overview.show_from')}
                    content={this.generatePeriodStartOptions()}
                    selected={this.state.startDate}
                    handleChange={(val) => this.onDateChange(val)}
                  />
                </div>
                <div>
                  {
                    (this.state.selectedProduct && this.state.selectedDeskGroup) &&
                      <div className={'d-flex flex-column ml-3'}>
                          <div className={'rates-overview-selected-item'}>
                            {t('l.rates_overview.product')}:
                              <button
                                  className={'text-primary list-link-item pl-1 rates-overview-selected-item-link'}
                                  onClick={() => window.open(`/setup/products/${(
                                    this.state.selectedProduct! as RatesSelectedItem).value}/view`, '_blank')}>
                                {(this.state.selectedProduct! as RatesSelectedItem).label}
                              </button>
                          </div>
                          <div>
                            {t('l.rates_overview.desk_group')}:
                              <button
                                  className={'text-primary list-link-item pl-1 rates-overview-selected-item-link'}
                                  onClick={() => window.open('/setup/deskgroups', '_blank')}>
                                {(this.state.selectedDeskGroup! as RatesSelectedItem).label}
                              </button>
                          </div>
                      </div>
                  }
                </div>
              </div>
              <div>
                {this.props.rateCacheStatus && (
                  <div className="d-flex flex-column justify-content-center align-items-center ml-4">
                    <CacheStatusLabel onPublish={this.onPublish} status={this.props.rateCacheStatus}
                                      canPublish={this.props.coreCacheStatus ? this.props.coreCacheStatus.unPublishedChangesFound : undefined}/>
                  </div>
                )}
              </div>
            </div>
            <div className="rates-overview-page">
              {(!this.state.rates.length) && (
                <div className="temp-container">
                  <>
                    {((!this.state.brokerRateCode || !this.state.deskCode) && !this.state.isLoading) && (
                      <div>{t('text.rates_overview.select_broker_and_desk')}</div>
                    )}
                    {this.state.isLoading && (
                      <Spinner animation="border" variant="info"/>
                    )}
                  </>
                </div>
              )}
              {this.state.rates.length !== 0 && (
                <div className="timeline-table-overview">
                  {this.state.ratePlansToBeRendered && (
                    <div className="sticky-timeline">
                      <RatesTimeline
                        isOverview
                        ratePlansSortedByDesk={this.state.ratePlansToBeRendered}
                        onTimelineObjectClick={(id: number) => {
                        }}
                        startDate={this.state.startDate.value}
                        endDate={this.state.endDate.toString()}
                        cacheUpdatedStarted={this.props.coreCacheStatus ? this.props.coreCacheStatus.updateStartedAt : undefined}
                      />
                    </div>
                  )}
                  <RatesOverviewTable
                    largestPerDay={this.state.largestPerDay}
                    smallestPerDay={this.state.smallestPerDay}
                    ratesToBeRendered={this.groupRatesByCarModel()}
                    carModels={this.props.carModels}
                    startingDate={this.state.startDate.value}
                    endDate={this.state.endDate.toString()}
                    deskCode={this.state.deskCode}
                    brokerRateCode={this.state.brokerRateCode}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
    );
  }

}

const mapStateToProps = (state: AppState) => ({
  ratePlans: state.rates.ratePlans.ratePlans,
  products: state.setup.products.products,
  deskGroups: state.setup.deskGroups.deskGroups,
  carModels: state.setup.carModels.carModels,
  brokerRates: state.setup.brokerRates.brokerRates,
  desks: state.setup.desks.desks,
  items: state.setup.items.items,
  selectedProductId: state.rates.ratePlans.productId,
  selectedDeskGroupId: state.rates.ratePlans.deskGroupId,
  rateCacheStatus: state.rates.rateCacheStatus.rateCacheStatus,
  selectedBrokerRateCode: state.rates.ratePlans.brokerRateCode,
  selectedDeskCode: state.rates.ratePlans.deskCode,
  selectedStartingDate: state.rates.ratePlans.startingDate,
  coreCacheStatus: state.rates.rateCacheStatus.coreCacheStatus
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, RatePlanActionTypes>) => ({
  getRatePlanList: (productId?: number) => dispatch(getRatePlanList(productId)),
  getProductList: () => dispatch(getProductList()),
  getDeskGroupList: () => dispatch(getDeskGroupList()),
  getCarModelList: () => dispatch(getCarModelsList()),
  getAllBrokerRates: () => dispatch(getAllBrokerRatesList()),
  getAllDesks: () => dispatch(getDeskList()),
  getItemPrices: () => dispatch(getItemPriceList()),
  getItemList: () => dispatch(getItemList()),
  saveProductId: (id: number) => dispatch(saveProductId(id)),
  getRateCacheStatus: () => dispatch(getRateCacheStatus()),
  storeDeskCode: (code: string) => dispatch(storeDeskCode(code)),
  storeBrokerRateCode: (code: string) => dispatch(storeBrokerRateCode(code)),
  storeStartingDate: (date: string) => dispatch(storeStartingDate(date)),
  saveDeskGroupId: (id: number) => dispatch(saveDeskGroupId(id)),
  getCoreCacheStatus: () => dispatch(getCoreCacheStatus())
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(RatesOverview));
